import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import GlobalStyles from './styles/GlobalStyles';
import Layout from './components/Layout';

// Page Imports
import Dashboard from './pages/Dashboard';
import Employees from './pages/Employees';
import Attendance from './pages/Attendance';
import LeaveRequests from './pages/LeaveRequests';
import Login from './pages/Login';
import Register from './pages/Register';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { CompanySettingsProvider } from './contexts/CompanySettingsContext';
import SalaryCalculator from './pages/Salary';
import RfidCards from './pages/RfidCards';
import CompanySettings from './pages/CompanySettings';
import Departments from './pages/Departments';
import Payment from './pages/Payment';
import Shifts from './pages/Shifts';
import { ThemeProvider as CustomThemeProvider } from './contexts/ThemeContext';
import SubscriptionAlert from './components/SubscriptionAlert';
import OfficialHolidays from './pages/OfficialHolidays';
import Visitors from './pages/Visitors';
import LeaveApproval from './pages/LeaveApproval';
import EntryExit from './pages/EntryExit';
import HealthSafety from './pages/HealthSafety';
import Performance from './pages/Performance';
import Projects from './pages/Projects';
import Tasks from './pages/Tasks';
import MeetingRooms from './pages/MeetingRooms';
import Cafeteria from './pages/Cafeteria';
import MealMenu from './pages/MealMenu';
import DeviceManagement from './pages/DeviceManagement';
import FacilityManagement from './pages/FacilityManagement';
import FacilityAccessControl from './pages/FacilityAccessControl';
import FacilityUsageReports from './pages/FacilityUsageReports';
import Overtime from './pages/Overtime';
import Timesheet from './pages/Timesheet';
import Training from './pages/Training';
import Integrations from './pages/Integrations';
import Notifications from './pages/Notifications';
import AuditLogs from './pages/AuditLogs';
import Reports from './pages/Reports';
import Profile from './pages/Profile';
import PaymentCallback from './pages/PaymentCallback';

const defaultTheme = createTheme({
  palette: {
    primary: {
      main: '#1976d2',
    },
    secondary: {
      main: '#dc004e',
    },
  },
});

function RequireAuth({ children }) {
  const { token } = useAuth();
  const location = useLocation();
  if (!token) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  return children;
}

function App() {
  // Sadece context provider ile sarıyoruz, tekrar ThemeProvider'a gerek yok
  return (
    <CustomThemeProvider>
      <CssBaseline />
      <GlobalStyles />
      <AuthProvider>
        <CompanySettingsProvider>
          <Router>
            <SubscriptionAlert />
            <Routes>
            {/* Public Routes */}
            <Route path="/login" element={<Login />} />
            
            {/* Protected Routes */}
            <Route path="/" element={<RequireAuth><Layout /></RequireAuth>}>
              {/* Dashboard */}
              <Route index element={<Dashboard />} />
              
              {/* Employee Management */}
              <Route path="employees" element={<Employees />} />
              <Route path="profile" element={<Profile />} />
              
              {/* Time & Attendance */}
              <Route path="attendance" element={<Attendance />} />
              <Route path="visitors" element={<Visitors />} />
              <Route path="entry-exit" element={<EntryExit />} />
              <Route path="shifts" element={<Shifts />} />
              <Route path="official-holidays" element={<OfficialHolidays />} />
              <Route path="overtime" element={<Overtime />} />
              <Route path="timesheet" element={<Timesheet />} />
              
              {/* Leave Management */}
              <Route path="leave-requests" element={<LeaveRequests />} />
              <Route path="leave-approval" element={<LeaveApproval />} />
              
              {/* Facility Management */}
              <Route path="meeting-rooms" element={<MeetingRooms />} />
              <Route path="cafeteria" element={<Cafeteria />} />
              <Route path="meal-menu" element={<MealMenu />} />
              <Route path="device-management" element={<DeviceManagement />} />
              <Route path="facility-management" element={<FacilityManagement />} />
              <Route path="facility-access-control" element={<FacilityAccessControl />} />
              <Route path="facility-usage-reports" element={<FacilityUsageReports />} />
              
              {/* Projects & Performance */}
              <Route path="projects" element={<Projects />} />
              <Route path="tasks" element={<Tasks />} />
              <Route path="performance" element={<Performance />} />
              <Route path="training" element={<Training />} />
              
              {/* Finance */}
              <Route path="salary" element={<SalaryCalculator />} />
              <Route path="payment" element={<Payment />} />
              
              {/* Security */}
              <Route path="rfidcards" element={<RfidCards />} />
              
              {/* Organization */}
              <Route path="departments" element={<Departments />} />
              <Route path="integrations" element={<Integrations />} />
              
              {/* System */}
              <Route path="register" element={<Register />} />
              <Route path="company-settings" element={<CompanySettings />} />
              <Route path="notifications" element={<Notifications />} />
              <Route path="audit-logs" element={<AuditLogs />} />
              <Route path="reports" element={<Reports />} />
              
              {/* Health & Safety */}
              <Route path="health-safety" element={<HealthSafety />} />
            </Route>
            {/* Payment return callback (can be public) */}
            <Route path="/payment/callback" element={<PaymentCallback />} />
            </Routes>
          </Router>
        </CompanySettingsProvider>
      </AuthProvider>
    </CustomThemeProvider>
  );
}

export default App;
